<template>
  <div v-if="pickerContentType">
    <v-card-title class="pa-1 mt-1">
      <h4
        :class="fieldError ? 'red--text wiggle' : 'white--text'"
        v-html="field.label"
      ></h4>
    </v-card-title>
    <v-card-subtitle v-if="field.helpText" class="pa-1">
      <p
        class="mt-1 grey--text text--lighten-1 text-caption font-weight-regular"
      >
        {{ field.helpText }}
      </p>
    </v-card-subtitle>

    <v-container v-if="loading" fill-height>
      <v-progress-circular :width="3" :size="25" indeterminate color="blue" />
    </v-container>

    <span v-if="!field.multivalued && !loading">
      <v-chip v-if="!!value" class="mr-2">
        <v-avatar v-if="!(props.field?.editable === false)">
          <a
            class="graylink cursor-as-pointer"
            target="_blank"
            @click="editContent(value, router, true)"
          >
            <v-icon small>edit</v-icon>
          </a>
        </v-avatar>

        <strong class="mx-2 overflow-x-hidden">
          <span>{{ label() }}</span>
        </strong>

        <v-icon
          v-if="$store.getters.permissionToWrite"
          small
          @click="
            newValue = null;
            changed = true;
          "
          >delete</v-icon
        >
      </v-chip>
    </span>

    <draggable
      v-else-if="!loading"
      v-model="newValue"
      :disabled="!store.getters.permissionToWrite"
    >
      <div v-for="(item, index) in newValue" :key="index" class="pa-1 mt-1">
        <v-chip class="mr-2">
          <v-avatar v-if="!(props.field?.editable === false)">
            <a
              class="graylink"
              target="_blank"
              @click="editContent(item, router, true)"
            >
              <v-icon small>edit</v-icon>
            </a>
          </v-avatar>

          <strong
            class="mx-2 overflow-x-hidden"
            :class="{ 'move-cursor': newValue.length > 1 }"
          >
            <span>{{ label(item.uuid) }}</span>
          </strong>

          <v-icon
            v-if="$store.getters.permissionToWrite"
            small
            @click="remove(index)"
            >delete</v-icon
          >
        </v-chip>
      </div>
    </draggable>

    <v-btn
      v-if="
        !loading &&
        store.getters.permissionToWrite &&
        (!props.value?.length ||
          !props.field?.max ||
          props.value?.length < props.field?.max)
      "
      icon
      @click="
        pickerPopup.toogle();
        changed = true;
      "
    >
      <v-icon :color="fieldError ? 'red' : ''">add_to_photos</v-icon>
    </v-btn>

    <v-card-subtitle v-if="fieldError" class="px-0 py-0">
      <span class="red--text">{{ fieldError }}</span>
    </v-card-subtitle>

    <v-text-field
      v-show="false"
      ref="pickerRules"
      v-model="newValue"
      :rules="field.rules"
      readonly
    />

    <PickerPopup
      ref="pickerPopup"
      :field="field"
      :content-type="pickerContentType"
      :action-on-info-table="selectInfoTableRecord"
    />
  </div>
  <div v-else>
    <v-card-title class="pa-1 mt-1">
      <h4
        :class="fieldError ? 'red--text wiggle' : 'white--text'"
        v-html="field.label"
      ></h4>
    </v-card-title>
    Field picker content type not found!
  </div>
</template>

<script setup>
import { useStore } from "vue2-helpers/vuex";
import { useRouter } from "vue2-helpers/vue-router";

import { ref, computed, onMounted, watch } from "vue";

import draggable from "vuedraggable";

import PickerPopup from "./Picker/PickerPopup.vue";

import {
  getContentTypeByConfigArg,
  editContent,
  getTitleField,
} from "@/utils/content";

// eslint-disable-next-line no-undef
const emit = defineEmits(["input", "updateNewContent"]);

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => {} },
  value: { type: [Object, Array, String], default: null },
  content: { type: Object, default: () => {} },
});

const store = useStore();
const router = useRouter();

// Vue HTML REFs
const pickerRules = ref(null);
const pickerPopup = ref(null);

const newValue = ref(props.value);
const { rawContentTypes } = store.state;
const pickerContentType = props.field.contentWtype
  ? getContentTypeByConfigArg(
      "wtype",
      props.field.contentWtype,
      rawContentTypes
    )
  : null;

const loading = ref(false); // Used to getExternalContent (ALPHA)
const changed = ref(false);

const fieldError = computed(() => {
  if (!changed.value) return null;

  if (pickerRules.value && pickerRules.value.valid) {
    return null;
  }
  return pickerRules.value && pickerRules.value.errorBucket.length
    ? pickerRules.value.errorBucket[0]
    : null;
});

const showInListFields = (fields) =>
  Object.entries(fields).reduce((acc, [key, field]) => {
    if (field.showInList) {
      acc[key] = field;
    }
    return acc;
  }, {});

const generateLabel = (item, inListFields) => {
  const string = Object.entries(inListFields).reduce(
    (acc, [key, value], index) => {
      if (index > 0) acc += " | ";

      if (value.isTitle && !item[key]) {
        acc += item.wtitle;
      } else {
        acc += item[key] || "NO DATA";
      }

      return acc;
    },
    ""
  );

  return string.trim() !== "" ? string : null;
};

const label = (uuid) => {
  const inListFields = showInListFields(pickerContentType.fields);
  let item = newValue.value;
  if (uuid) {
    item = newValue.value.find((e) => e.uuid === uuid);
  }

  return generateLabel(item, inListFields);
};

const selectInfoTableRecord = (item) => {
  const wtitleKey = getTitleField(pickerContentType);
  if (wtitleKey && !item.hasOwnProperty(wtitleKey)) {
    item[wtitleKey] = item.wtitle;
  }

  if (!props.field.multivalued) {
    newValue.value = item;
  } else {
    // Bug Fix
    if (!newValue.value) newValue.value = [];

    if (!newValue.value.some((e) => e.uuid === item.uuid))
      newValue.value.push(item);
  }

  pickerPopup.value.toogle();
};

const remove = (index) => {
  changed.value = true;
  newValue.value.splice(index, 1);
};

// onMounted(() => {
//   // const pickerContentType = getContentTypeByConfigArg('wtype', props.field.contentWtype, rawContentTypes.value);
//   // console.log('pickerContentType> ', pickerContentType);
// });

watch(newValue, (value) => {
  emit("input", value);
  const { content } = props;
  if (value) {
    if (props.field.fieldToSync && props.field.fieldToSync.length) {
      props.field.fieldToSync.map((field) => {
        if (value[field.updateField]) {
          content[field.retrieveField] = value[field.updateField];
        }
      });
      emit("updateNewContent", content);
    }
  } else {
    props.field.fieldToSync?.map((field) => {
      content[field.retrieveField] = null;
    });
    emit("updateNewContent", content);
  }
  emit("updateNewContent", content);
});
</script>

<style lang="scss" scoped></style>
